import React from 'react';
import {view, SelectField, tokens} from '@sail/ui';
import {upper} from 'src/utils/helpers';
import {SelectFieldItemWithTooltip} from 'src/v2/components/common';
import {getNetworkIcon} from 'src/components/Wallet/constants';
import {getCurrencyIcon} from 'src/v2/util/getCurrencyIcon';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import type {DirectDepositOptions} from 'src/v2/components/directDeposit/useDirectDeposit';

import {message, useIntl} from '@sail/intl';

const messages = {
  network: message({
    id: 'crypto.payins.v2.direct_deposit.network',
    description: 'Label for network selector',
    defaultMessage: 'Network',
  }),

  networkPlaceholder: message({
    id: 'crypto.payins.v2.direct_deposit.network_placeholder',
    description: 'Placeholder for network selector when no network is selected',
    defaultMessage: 'Select',
  }),

  currency: message({
    id: 'crypto.payins.v2.direct_deposit.currency',
    description: 'Label for currency selector',
    defaultMessage: 'Currency',
  }),

  currencyPlaceholder: message({
    id: 'crypto.payins.v2.direct_deposit.currency_placeholder',

    description:
      'Placeholder for currency selector when no currency is selected',

    defaultMessage: 'Select',
  }),
};

const DISABLED_COLOR = tokens.color.neutral[400].toString();

export const NetworkCurrencySelectors = ({
  selectedNetwork,
  setSelectedNetwork,
  networkOptions,
  selectedCurrency,
  setSelectedCurrency,
  currencyOptions,
}: DirectDepositOptions) => {
  const intl = useIntl();
  return (
    <view.div css={{stack: 'x', gapX: 'small'}}>
      <SelectField
        label={intl.formatMessage(messages.network)}
        selectedKey={selectedNetwork}
        placeholder={intl.formatMessage(messages.networkPlaceholder)}
        onSelectionChange={(key) => {
          const option = networkOptions.find((opt) => opt.network === key);
          if (option && !option.isDisabled) {
            setSelectedNetwork(option.network);
          }
        }}
        size="small"
        css={{flex: '1 1 0', minWidth: 'space.0'}}
        subviews={{
          button: {
            css: {
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 400,
              color: selectedNetwork ? 'text' : DISABLED_COLOR,
            },
          },
        }}
      >
        {[...networkOptions]
          .sort((a, b) => Number(a.isDisabled) - Number(b.isDisabled))
          .map(({network, isDisabled, compatibleCurrencies}) => {
            const disabledTooltip =
              isDisabled && compatibleCurrencies
                ? `${upper(
                    network,
                  )} is only available with ${compatibleCurrencies
                    .map((c) => c.toUpperCase())
                    .join(', ')}`
                : undefined;

            return (
              <view.div key={network}>
                <SelectFieldItemWithTooltip
                  id={network}
                  label={upper(network)}
                  isDisabled={isDisabled}
                  disabledTooltip={disabledTooltip}
                  iconElement={getNetworkIcon(network)}
                />
              </view.div>
            );
          })}
      </SelectField>
      <SelectField
        label={intl.formatMessage(messages.currency)}
        selectedKey={selectedCurrency}
        placeholder={intl.formatMessage(messages.currencyPlaceholder)}
        onSelectionChange={(key) => {
          const option = currencyOptions.find((opt) => opt.currency === key);
          if (option && !option.isDisabled) {
            setSelectedCurrency(option.currency);
          }
        }}
        size="small"
        css={{flex: '1 1 0', minWidth: 'space.0'}}
        subviews={{
          button: {
            css: {
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 400,
              color: selectedCurrency ? 'text' : DISABLED_COLOR,
            },
          },
        }}
      >
        {[...currencyOptions]
          .sort((a, b) => Number(a.isDisabled) - Number(b.isDisabled))
          .map(({currency, isDisabled, compatibleNetworks}) => {
            const disabledTooltip =
              isDisabled && compatibleNetworks
                ? `${formatAssetCode(
                    currency,
                  )} is only available with ${compatibleNetworks
                    .map((n) => upper(n))
                    .join(', ')}`
                : undefined;

            return (
              <view.div key={currency}>
                <SelectFieldItemWithTooltip
                  id={currency}
                  label={formatAssetCode(currency)}
                  isDisabled={isDisabled}
                  disabledTooltip={disabledTooltip}
                  iconElement={getCurrencyIcon(currency)}
                />
              </view.div>
            );
          })}
      </SelectField>
    </view.div>
  );
};
